import type { WorkspaceDraftDocument, WorkspaceThreadDraft } from './workspace.types';

/** Newest non-superseded, non-created draft — the only Creatable revision. */
export function findLiveDraft(
  drafts: WorkspaceThreadDraft[]
): WorkspaceThreadDraft | undefined {
  for (let i = drafts.length - 1; i >= 0; i -= 1) {
    const draft = drafts[i];
    if (!draft || draft.superseded || draft.status === 'created') continue;
    return draft;
  }
  return undefined;
}

export function findLiveDraftIndex(drafts: WorkspaceThreadDraft[]): number {
  for (let i = drafts.length - 1; i >= 0; i -= 1) {
    const draft = drafts[i];
    if (!draft || draft.superseded || draft.status === 'created') continue;
    return i;
  }
  return -1;
}

/** Strip thread metadata for the legacy `workspace.component` mirror. */
export function toDraftDocument(draft: WorkspaceThreadDraft): WorkspaceDraftDocument {
  return {
    kind: 'draft-document',
    documentKind: draft.documentKind,
    title: draft.title,
    subtitle: draft.subtitle,
    sessionId: draft.sessionId,
    content: draft.content,
    status: draft.status,
    steps: draft.steps,
    createError: draft.createError,
  };
}

export function toThreadDraft(
  document: WorkspaceDraftDocument,
  meta: {
    id: string;
    ts: number;
    version?: number;
    replacesId?: string;
    superseded?: boolean;
  }
): WorkspaceThreadDraft {
  return {
    ...document,
    id: meta.id,
    ts: meta.ts,
    version: meta.version ?? 1,
    replacesId: meta.replacesId,
    superseded: meta.superseded ?? false,
  };
}

/**
 * Append a new draft, or supersede the live one and re-anchor a revision.
 * Returns the updated stack + the new draft id.
 */
export function pushOrReviseDraft(
  drafts: WorkspaceThreadDraft[],
  document: WorkspaceDraftDocument,
  nextId: () => string,
  now = Date.now()
): { drafts: WorkspaceThreadDraft[]; id: string } {
  const live = findLiveDraft(drafts);
  if (!live) {
    const id = nextId();
    return {
      id,
      drafts: [...drafts, toThreadDraft(document, { id, ts: now, version: 1 })],
    };
  }

  const version = (live.version ?? 1) + 1;
  const id = nextId();
  const revised = toThreadDraft(document, {
    id,
    ts: now,
    version,
    replacesId: live.id,
    superseded: false,
  });

  return {
    id,
    drafts: drafts
      .map((draft) =>
        draft.id === live.id
          ? { ...draft, superseded: true, version: draft.version ?? 1 }
          : draft
      )
      .concat(revised),
  };
}
